import React from "react";
import { indian_cities_dropdown_options } from "repoV2/features/Common/modules/Location/constants/indianCitiesData";
import {
    IBookingQuestion,
    ILeadsQuestion,
} from "repoV2/features/CheckoutPages/utils/apis/CustomQuestionsApi/ICustomQuestionsApi.interface";
import { indian_states_and_uts_dropdown_options } from "repoV2/features/Common/modules/Location/constants/indianStatesUtsData";
import { IExlySearchableDropdownOption } from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import {
    CUSTOM_QUESTION_TYPES,
    CUSTOM_QUESTION_TYPES_ID,
    other_dropdown_option,
} from "../../constants/CustomQuestionsTypes.constants";
import ParagraphText from "../ParagraphText/ParagraphText";
import ShortTextQuestion from "../ShortTextQuestion/ShortTextQuestion";
import SingleSelectDropdown from "../SingleSelectDropdown/SingleSelectDropdown";
import NumberQuestion from "../NumberQuestion/NumberQuestion";
import SingleSelectOption from "../SingleSelectOption/SingleSelectOption";
import MultipleSelectOption from "../MultipleSelectOption/MultipleSelectOption";
import CalendarQuestion from "../CalendarQuestion/CalendarQuestion";
import FileUploadQuestion from "../FileUploadQuestion/FileUploadQuestion";
import { ICustomQuestionFormGroup } from "./CustomQuestionFormGroup.interfaces";
import { getOtherOptionFieldNameWithSuffix } from "../../utils/CustomQuestions.utils";
import { gender_dropdown_options } from "../../constants/CustomQuestions.constants";

const indian_cities_with_other_options = [
    ...indian_cities_dropdown_options,
    other_dropdown_option,
];

const gender_choices_with_other_options = [
    ...gender_dropdown_options,
    other_dropdown_option,
];

const CustomQuestionFormGroup = (props: ICustomQuestionFormGroup) => {
    const {
        questions = [],
        getInputFieldName,
        setDetails,
        setFileUploaderFileValue,
        clearFileUploaderFileValue,
        customQuestionHelperProps,
        generateUploadKey,
        setIsCustomQuestionFileUploading,
    } = props;

    if (checkIsArrayEmpty(questions)) return null;

    const retrunJSX = questions.map(
        (question: IBookingQuestion | ILeadsQuestion) => {
            switch (question.type) {
                case CUSTOM_QUESTION_TYPES_ID.PARAGRAPH_TEXT: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.PARAGRAPH_TEXT
                        ];
                    return (
                        <ParagraphText
                            title={question.question}
                            placeholder="Your Answer"
                            name={getInputFieldName(question.uuid)}
                            onBlur={() =>
                                setDetails({ fieldName: question.uuid })
                            }
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.PARAGRAPH_TEXT
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            key={question.uuid}
                            maxLength={+question?.validations?.maxlength}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.SHORT_TEXT: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.SHORT_TEXT
                        ];
                    return (
                        <ShortTextQuestion
                            title={question.question}
                            placeholder="Your Answer"
                            name={getInputFieldName(question.uuid)}
                            onBlur={() =>
                                setDetails({ fieldName: question.uuid })
                            }
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.SHORT_TEXT
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            key={question.uuid}
                            maxLength={+question?.validations?.maxlength}
                            autoComplete="on"
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.INDIAN_STATE: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.INDIAN_STATE
                        ];
                    return (
                        <SingleSelectDropdown
                            name={getInputFieldName(question.uuid)}
                            options={indian_states_and_uts_dropdown_options}
                            placeholder={
                                question.is_mandatory
                                    ? "State"
                                    : "State (Optional)"
                            }
                            onChange={(option: IExlySearchableDropdownOption) =>
                                setDetails({
                                    fieldName: question.uuid,
                                    value: option.value,
                                })
                            }
                            key={question.uuid}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.INDIAN_STATE
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            classes={classes}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY
                        ];
                    return (
                        <SingleSelectDropdown
                            name={getInputFieldName(question.uuid)}
                            options={indian_cities_with_other_options}
                            placeholder={
                                question.is_mandatory
                                    ? "City"
                                    : "City (Optional)"
                            }
                            onChange={(option: IExlySearchableDropdownOption) =>
                                setDetails({
                                    fieldName: question.uuid,
                                    value: option.value,
                                })
                            }
                            key={question.uuid}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            otherOptionValue={other_dropdown_option.value}
                            customOtherPlaceholder="Enter Your Answer"
                            customOtherName={getOtherOptionFieldNameWithSuffix(
                                getInputFieldName(question.uuid)
                            )}
                            customOtherOnBlur={() =>
                                setDetails({
                                    fieldName:
                                        getOtherOptionFieldNameWithSuffix(
                                            question.uuid
                                        ),
                                })
                            }
                            customOtherValidator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            classes={classes}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.GENDER: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.GENDER
                        ];
                    return (
                        <SingleSelectDropdown
                            name={getInputFieldName(question.uuid)}
                            options={gender_choices_with_other_options}
                            placeholder={
                                question.is_mandatory
                                    ? "Gender"
                                    : "Gender (Optional)"
                            }
                            onChange={(option: IExlySearchableDropdownOption) =>
                                setDetails({
                                    fieldName: question.uuid,
                                    value: option.value,
                                })
                            }
                            key={question.uuid}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.GENDER
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            otherOptionValue={other_dropdown_option.value}
                            customOtherPlaceholder="Enter Your Answer"
                            customOtherName={getOtherOptionFieldNameWithSuffix(
                                getInputFieldName(question.uuid)
                            )}
                            customOtherOnBlur={() =>
                                setDetails({
                                    fieldName:
                                        getOtherOptionFieldNameWithSuffix(
                                            question.uuid
                                        ),
                                })
                            }
                            customOtherValidator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.GENDER
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            classes={classes}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.NUMBER: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.NUMBER
                        ];
                    return (
                        <NumberQuestion
                            title={question.question}
                            placeholder="Your Answer"
                            name={getInputFieldName(question.uuid)}
                            onBlur={() =>
                                setDetails({ fieldName: question.uuid })
                            }
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.NUMBER
                            ].validator({
                                isRequired: question.is_mandatory,
                                min: +question?.validations?.min,
                                max: +question?.validations?.max,
                            })}
                            key={question.uuid}
                            max={+question?.validations?.max}
                            min={+question?.validations?.min}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.SINGLE_OPTION_SELECT: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.SINGLE_OPTION_SELECT
                        ];
                    return (
                        <SingleSelectOption
                            name={getInputFieldName(question.uuid)}
                            title={question.question}
                            options={question.choices || []}
                            onChange={(value: string) => {
                                setDetails({ fieldName: question.uuid, value });
                            }}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.SINGLE_OPTION_SELECT
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            key={question.uuid}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.URL: {
                    const { classes } =
                        customQuestionHelperProps[CUSTOM_QUESTION_TYPES_ID.URL];
                    return (
                        <ShortTextQuestion
                            title={question.question}
                            placeholder="Enter URL"
                            name={getInputFieldName(question.uuid)}
                            onBlur={() =>
                                setDetails({ fieldName: question.uuid })
                            }
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.URL
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            autoComplete="url"
                            key={question.uuid}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.MULTI_OPTION_SELECT: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.MULTI_OPTION_SELECT
                        ];
                    return (
                        <MultipleSelectOption
                            name={getInputFieldName(question.uuid)}
                            title={question.question}
                            options={question.choices || []}
                            onChange={(value: Array<string>) => {
                                setDetails({ fieldName: question.uuid, value });
                            }}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.MULTI_OPTION_SELECT
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            key={question.uuid}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.CALENDAR: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.CALENDAR
                        ];
                    return (
                        <CalendarQuestion
                            name={getInputFieldName(question.uuid)}
                            title={question.question}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.CALENDAR
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            key={question.uuid}
                            onChange={(value: string | Date) => {
                                setDetails({ fieldName: question.uuid, value });
                            }}
                            classes={classes}
                            isRequired={question.is_mandatory}
                        />
                    );
                }
                case CUSTOM_QUESTION_TYPES_ID.FILE: {
                    const { classes } =
                        customQuestionHelperProps[
                            CUSTOM_QUESTION_TYPES_ID.FILE
                        ];
                    return (
                        <FileUploadQuestion
                            name={getInputFieldName(question.uuid)}
                            title={question.question}
                            onChange={({
                                fileKey,
                                fileData,
                            }: {
                                fileKey: string;
                                fileData: {
                                    type: string;
                                    value: string;
                                    thumbnail?: string;
                                };
                            }) => {
                                setFileUploaderFileValue({
                                    fieldName: question.uuid,
                                    fileData,
                                    value: fileKey,
                                });
                            }}
                            validator={CUSTOM_QUESTION_TYPES[
                                CUSTOM_QUESTION_TYPES_ID.FILE
                            ].validator({
                                isRequired: question.is_mandatory,
                            })}
                            onClear={() => {
                                clearFileUploaderFileValue({
                                    fieldName: question.uuid,
                                });
                            }}
                            generateUploadKey={generateUploadKey}
                            key={question.uuid}
                            classes={classes}
                            isRequired={question.is_mandatory}
                            handleFileUploadStart={() =>
                                setIsCustomQuestionFileUploading({
                                    [question.uuid]: true,
                                })
                            }
                            handleFileUploadEnd={() =>
                                setIsCustomQuestionFileUploading({
                                    [question.uuid]: false,
                                })
                            }
                            uploadTypeAccept={question.validations?.accept}
                        />
                    );
                }
                default:
                    return null;
            }
        }
    );
    return <>{retrunJSX}</>;
};

export default CustomQuestionFormGroup;
